import type { CalendarDay } from "../api/types";
import { formatDurationShort } from "../lib/duration";
import { WEEKDAY_LABELS, mondayFirstWeekday, parseIsoDate } from "../lib/dates";

interface Props {
  days: CalendarDay[];
  selected: string | null;
  onSelect: (workDate: string) => void;
}

const TODAY_ISO = new Date().toLocaleDateString("en-CA");

export function CalendarGrid({ days, selected, onSelect }: Props): JSX.Element {
  // Pad the first week so the 1st lands under the right weekday.
  const leadingBlanks = days.length > 0 ? mondayFirstWeekday(parseIsoDate(days[0].work_date)) : 0;

  return (
    <div className="calendar">
      <div className="calendar-weekdays" role="row">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="calendar-weekday" role="columnheader">
            {label}
          </div>
        ))}
      </div>

      <div className="calendar-grid">
        {Array.from({ length: leadingBlanks }, (_, index) => (
          <div key={`blank-${index}`} className="calendar-cell blank" aria-hidden="true" />
        ))}

        {days.map((day) => {
          const dayNumber = Number(day.work_date.slice(8, 10));
          const classes = ["calendar-cell"];
          if (day.work_date === selected) classes.push("selected");
          if (day.work_date === TODAY_ISO) classes.push("today");
          if (day.locked) classes.push("locked");
          if (day.minutes > 0) classes.push("has-entries");

          return (
            <button
              type="button"
              key={day.work_date}
              className={classes.join(" ")}
              onClick={() => onSelect(day.work_date)}
              aria-pressed={day.work_date === selected}
              aria-label={`${day.work_date}, ${day.duration_label}${day.locked ? ", locked" : ""}`}
            >
              <span className="calendar-daynum">{dayNumber}</span>
              {day.locked && (
                <span className="lock-badge" title={day.is_override ? "Locked by an admin" : "Locked automatically"}>
                  {day.is_override ? "🔒*" : "🔒"}
                </span>
              )}
              <span className="calendar-total">{formatDurationShort(day.minutes)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
